import { deviceSession } from '@/privacy/deviceStorage';
import { useEffect } from 'react';
import { NavigationType, useLocation, useNavigationType } from 'react-router-dom';
import { barTitleOf } from '@featit/shared/bar-title';
import { chromeLabelText, type ChromeTranslateFn } from '@featit/shared/chrome-label';
import { useT } from '@/i18n/LocaleProvider';
import { linkOpenOf } from '@/routing/internalAddress';
import type { IPhoneChromeRows } from './phoneChromeCore';
import { backLabelOfAddress } from './historyBackLabel';
import { historyIndex } from './backNavigation';

/**
 * I-1304 (`docs/concept-iphone-ux.md`, IL-16, the captain's ruling): a root reached by a link has
 * the back row. A tab root or a drawer root has none, because there is no page of the reader's own
 * under it; a link put one there, so the root is drawn as a pushed page is — ‹ and the linking
 * page, the root's name — and Back returns to it. A back row is drawn exactly when there is a page
 * to go back to. The link's push says so (`linkOpenOf`); More, a tab and the shell's own
 * `navigate` never do.
 */
export function linkedRootRowsOf(
  t: ChromeTranslateFn,
  rows: IPhoneChromeRows,
  pathname: string,
  state: unknown,
): IPhoneChromeRows {
  const link = linkOpenOf(state);
  if (!link) return rows;
  // A record pushed by a link has its row already; the row names the page Back returns to from the
  // history entry (IL-18, `historyBackLabel.ts`), as every pushed row does.
  if (rows.backRow !== null) return rows;
  if (rows.topRow === null && rows.pageRoot === null) return rows;
  // Where Back goes: the linking record's key, or the linking page's name (IL-15).
  const label = backLabelOfAddress(link.from, t);
  return {
    ...rows,
    topRow: null,
    pageRoot: null,
    backRow: {
      path: link.from,
      label,
      recordKey: rows.pageRoot?.title || chromeLabelText(barTitleOf(pathname).label, t),
      recordType: 'page',
    },
  };
}

/**
 * The entry's mark, kept for the entry by its history index: a root rewrites its own query as it
 * settles (the Roadmap's range), a replace that drops the router state the link left. The index
 * stays the same across a replace and a reload; a push that lands on the index without a mark — More,
 * a tab, the shell's `navigate` — clears it, so no later page inherits it.
 */
const KEPT_PREFIX = 'featit:link-open:';

function keptMarkOf(index: number, pathname: string): unknown {
  try {
    const kept = JSON.parse(deviceSession.getItem(`${KEPT_PREFIX}${index}`) ?? 'null') as {
      pathname?: string;
      state?: unknown;
    } | null;
    return kept && kept.pathname === pathname ? kept.state : null;
  } catch {
    return null;
  }
}

function keepMark(index: number, pathname: string, state: unknown): void {
  try {
    if (state === null) deviceSession.removeItem(`${KEPT_PREFIX}${index}`);
    else deviceSession.setItem(`${KEPT_PREFIX}${index}`, JSON.stringify({ pathname, state }));
  } catch {
    // Without storage the mark lasts until the page rewrites its own address.
  }
}

export function useLinkedRootRows(rows: IPhoneChromeRows): IPhoneChromeRows {
  const { pathname, state } = useLocation();
  const navigationType = useNavigationType();
  const t = useT();
  const index = historyIndex();
  const mark = linkOpenOf(state) ?? linkOpenOf(keptMarkOf(index, pathname));
  useEffect(() => {
    if (linkOpenOf(state)) keepMark(index, pathname, state);
    else if (navigationType === NavigationType.Push) keepMark(index, pathname, null);
  }, [index, pathname, state, navigationType]);
  return linkedRootRowsOf(t, rows, pathname, mark);
}
